import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  CircularProgress,
} from "@mui/material";
import Papa from "papaparse";
import config from "../../Config/config";

const AdminCsvUpload = ({ refresh, category, subactivities, setAlert }) => {
  const [csvData, setCsvData] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setCsvData(result.data);
        setPreviewOpen(true);
        setResults([]);
      },
      error: (error) => {
        setAlert({
          type: "error",
          message: `Error parsing CSV: ${error.message}`,
        });
      },
    });
  };

  const handleConfirmUpload = async () => {
    setUploading(true);
    const tempResults = [];

    for (const row of csvData) {
      const cleanRow = { ...row };
      cleanRow.category_id = category.category_id;
      if (
        "parent_subactivity_code" in cleanRow &&
        cleanRow.parent_subactivity_code === "" &&
        "parent_subactivity_name" in cleanRow &&
        cleanRow.parent_subactivity_name === ""
      ) {
        cleanRow.parent_subactivity_id = null;
      }
      if (
        "parent_subactivity_code" in cleanRow &&
        cleanRow.parent_subactivity_code !== "" &&
        "parent_subactivity_name" in cleanRow &&
        cleanRow.parent_subactivity_name !== ""
      ) {
        const parentSubactivity = subactivities.find(
          (s) =>
            s.category_id === category.parent_category_id &&
            s.subactivity_code === cleanRow.parent_subactivity_code &&
            s.parent_subactivity_name === cleanRow.parent_subactivity_name,
        );
        cleanRow.parent_subactivity_id = parentSubactivity.subactivity_id;
      }

      try {
        const res = await fetch(`${config.serverBaseUrl}/subactivity`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(cleanRow),
        });

        const message = res.ok
          ? "✅ Successfully uploaded"
          : `❌ ${res.status}: ${await res.text()}`;

        tempResults.push({ row: cleanRow, success: res.ok, message });
        setPreviewOpen(false);
        refresh();
      } catch (err) {
        tempResults.push({
          row: cleanRow,
          success: false,
          message: `❌ ${err.message}`,
        });
        setPreviewOpen(false);
      }
    }

    setResults(tempResults);
    setUploading(false);
  };

  return (
    <Box sx={{ padding: 3, border: "1px solid #ccc", borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Upload Data In Bulk
      </Typography>

      <Button variant="contained" component="label">
        Choose CSV File
        <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
      </Button>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Preview CSV Upload</DialogTitle>
        <DialogContent>
          {csvData.length === 0 ? (
            <Typography>No data loaded</Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  {Object.keys(csvData[0]).map((key) => (
                    <TableCell key={key}>{key}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {csvData.map((row, idx) => (
                  <TableRow key={idx}>
                    {Object.values(row).map((value, i) => (
                      <TableCell key={i}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setPreviewOpen(false)}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            disabled={uploading}
          >
            {uploading ? <CircularProgress size={24} /> : "Proceed with Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {results.length > 0 && (
        <Box mt={3}>
          <Typography variant="h6">Upload Results</Typography>
          {results.map((res, index) => (
            <Alert
              key={index}
              severity={res.success ? "success" : "error"}
              sx={{ mb: 1 }}
            >
              Row {index + 1}: {res.message}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AdminCsvUpload;
